/**
 * The conversational onboarding Giga runs the first time someone lands in the
 * chat. It is an interview, not a form: each turn asks one question, offers the
 * likely answers as chips, and still accepts free text in the composer.
 *
 * It does two jobs at once. It collects what Giga needs (role, company, what to
 * watch, what to automate), and along the way it teaches the three ideas a new
 * user has to hold — Giga takes work off your hands, tracks hold the memory,
 * pulses run the work on a schedule, connections are what it reaches into.
 *
 * Role is asked first because everything after it branches on the answer.
 */

export type InterviewRole = 'Founder or CEO' | 'Operations' | 'Sales' | 'Marketing' | 'Engineering' | 'Customer support';
export type InterviewStep = {
  key: string;
  /** What Giga did before asking, shown as ticked work lines. */
  work?: {
    icon: string;
    label: string;
  }[];
  /** Question paragraphs. `**bold**` renders bold in the thread. */
  question: string[];
  /** A concept being introduced — rendered as a titled explainer above the chips. */
  concept?: {
    label: string;
    title: string;
    lines: string[];
  };
  /** Suggested answers shared by every role. */
  choices?: string[];
  /** Suggested answers that depend on the role given in step one. */
  choicesByRole?: Record<InterviewRole, string[]>;
  /** Chips accumulate and are confirmed together. */
  multi?: boolean;
  /** Multi-select questions start with everything ticked — opt out, not in. */
  preselectAll?: boolean;
  /** Placeholder shown in the composer while this question is open. */
  placeholder: string;
};
export const ONBOARDING_OPENER = '@giga onboard me';
export const INTERVIEW_ROLES: InterviewRole[] = ['Founder or CEO', 'Operations', 'Sales', 'Marketing', 'Engineering', 'Customer support'];

/** Work Giga can take on, per role. Shown as opt-out chips. */
const USE_CASES_BY_ROLE: Record<InterviewRole, string[]> = {
  'Founder or CEO': ['Board and investor updates from real numbers', 'Tell me what is slipping across the company', 'Brief me before every external meeting', 'Watch cash, pipeline and headcount together'],
  Operations: ['Keep systems of record in sync', 'Chase the steps that stall every week', 'Track vendors, contracts and renewals', 'Turn recurring processes into workflows'],
  Sales: ['Prep me for every call from the CRM and email history', 'Update Salesforce after calls so I do not have to', 'Flag deals that have gone quiet', 'Draft the follow-up while the call is fresh'],
  Marketing: ['Turn customer conversations into content', 'Report on campaigns across every channel', 'Watch what competitors ship and say', 'Keep the content calendar moving'],
  Engineering: ['Triage incoming bugs into Linear', 'Summarise what shipped this week', 'Answer questions from the codebase and docs', 'Chase reviews and stale pull requests'],
  'Customer support': ['Draft replies from how we solved it before', 'Spot themes across this week\'s tickets', 'Escalate anything breaking an SLA', 'Keep help docs current with real answers']
};

/** Work every role has, regardless of function. */
const UNIVERSAL_USE_CASES = ['Handle my admin — inbox, scheduling, follow-ups', 'Chase the things I am waiting on', 'Write my weekly update from what actually happened'];

/** First tracks worth opening, per role. */
const TRACKS_BY_ROLE: Record<InterviewRole, string[]> = {
  'Founder or CEO': ['Board reporting', 'Fundraising', 'Hiring — Q3', 'Each key customer'],
  Operations: ['Vendors and renewals', 'Onboarding process', 'Tooling and spend', 'Each key customer'],
  Sales: ['Each open opportunity', 'Q3 pipeline', 'Competitive intel', 'Renewals at risk'],
  Marketing: ['Q3 campaign', 'Content calendar', 'Competitive intel', 'Website and SEO'],
  Engineering: ['Current release', 'Incidents', 'Platform migration', 'Hiring — engineering'],
  'Customer support': ['Escalations', 'Recurring issues', 'Help centre', 'Each key customer']
};

/** Things worth running on a schedule, per role. */
const PULSES_BY_ROLE: Record<InterviewRole, string[]> = {
  'Founder or CEO': ['Monday company recap', 'Weekly metrics digest', 'Alert me when a deal over $50k moves', 'Monthly board pack draft'],
  Operations: ['Monday operations recap', 'Renewals due in 30 days', 'Flag processes that stalled', 'Weekly spend summary'],
  Sales: ['Monday pipeline recap', 'Daily call prep, 30 minutes before', 'Alert me when a deal goes quiet', 'Friday CRM hygiene sweep'],
  Marketing: ['Monday campaign recap', 'Weekly competitor digest', 'Alert me on brand mentions', 'Monthly content performance'],
  Engineering: ['Monday engineering recap', 'Daily stale review sweep', 'Alert me on new P1 bugs', 'Weekly shipped summary'],
  'Customer support': ['Monday support recap', 'Daily SLA risk check', 'Weekly ticket themes', 'Alert me on repeat escalations']
};
export const ONBOARDING_INTERVIEW: InterviewStep[] = [
// 1 — Role. Everything downstream branches on this, so it comes first.
{
  key: 'role',
  work: [{
    icon: 'gmail',
    label: 'Read your Gmail, Drive and Calendar'
  }, {
    icon: 'googlecalendar',
    label: 'Looked at the last 30 days of meetings'
  }],
  question: ['**Good — let\'s make me useful.**', 'I can already read your Gmail, Drive and Calendar. What I don\'t know yet is what matters to you. A few short questions and I\'ll set myself up around your work.', '**First: what do you do here?**'],
  choices: INTERVIEW_ROLES,
  placeholder: 'Tell me your role…'
},
// 2 — Company.
{
  key: 'company',
  question: ['**And what does the company do?**', 'A sentence is enough — what you sell and who buys it. I\'ll go deeper myself from there.'],
  choices: ['Read it from our website', 'Read it from the deck in Drive'],
  placeholder: 'We sell… to…'
},
// 3 — What Giga can do. The first teaching moment: this is a co worker that
// takes work off you, not a search box.
{
  key: 'usecases',
  work: [{
    icon: 'googlechrome',
    label: 'Read brightwave.io and 4 sub-pages'
  }, {
    icon: 'googledrive',
    label: 'Read "Q3 Board Review.pdf"'
  }],
  question: ['Got it — **B2B logistics software, sold to mid-market operations teams.** Written to `company/overview.md`.', '**So here\'s what I can take off your hands.**', 'Everyone gets the admin. The rest is what people in your role usually hand me first. I\'ve ticked them all — untick anything you don\'t want.'],
  concept: {
    label: 'How to think about me',
    title: 'I do the work, I don\'t just answer questions',
    lines: ['Ask me for something and I\'ll go and do it across your tools — read, draft, update, chase, report back.']
  },
  choicesByRole: Object.fromEntries(INTERVIEW_ROLES.map(r => [r, [...UNIVERSAL_USE_CASES, ...USE_CASES_BY_ROLE[r]]])) as Record<InterviewRole, string[]>,
  multi: true,
  preselectAll: true,
  placeholder: 'Or tell me something else you want off your plate…'
},
// 4 — Tracks. The second teaching moment: where memory lives, and who can see it.
{
  key: 'tracks',
  question: ['**Now, where all of that gets remembered.**', '**Which should I open first?** They fill themselves in from here on.'],
  concept: {
    label: 'Tracks',
    title: 'A track per project, customer or area',
    lines: ['Everything I learn gets filed into the track it belongs to, so the context is already there next time anyone asks.', 'Whoever can see the track can use what it knows — that\'s how access works.']
  },
  choicesByRole: TRACKS_BY_ROLE,
  multi: true,
  preselectAll: true,
  placeholder: 'Or name a project, customer or area…'
},
// 5 — Pulses. The third teaching moment: work that happens without being asked.
{
  key: 'pulses',
  work: [{
    icon: 'folder',
    label: 'Opened your tracks'
  }],
  question: ['Done — those are open and already filling.', '**What should I run without being asked?**'],
  concept: {
    label: 'Pulses',
    title: 'The work that should just happen',
    lines: ['A pulse is a job on a schedule — a recap every Monday, a check every morning, an alert the moment something moves.', 'It runs whether or not you remember to ask, and reports back where you already are.']
  },
  choicesByRole: PULSES_BY_ROLE,
  multi: true,
  preselectAll: true,
  placeholder: 'Or describe something you want automated…'
},
// 6 — Connections. What Giga can actually reach, and who owns it.
{
  key: 'connections',
  question: ['**Last piece: what I can reach.**', 'Gmail, Calendar and Drive are already connected. **What else should I have?** I\'ll walk you through each one after this.'],
  concept: {
    label: 'Connections',
    title: 'Connected once, for the whole company',
    lines: ['Connections belong to the workspace, not to you — so nobody re-authorises the same tool five times, and there\'s one place to see what I can reach.']
  },
  choices: ['Slack', 'Notion', 'Salesforce', 'HubSpot', 'Linear', 'Jira', 'Intercom', 'Airtable', 'Stripe', 'GitHub'],
  multi: true,
  placeholder: 'Or name a tool…'
},
// 7 — The permission boundary.
{
  key: 'autonomy',
  question: ['**How much should I do on my own?**', 'This sets what I can do without checking with you. Change it any time — it applies to everyone in the workspace.'],
  choices: ['Ask me before every action', 'Act on read-only work, ask before writing', 'Act, then tell me what you did'],
  placeholder: 'Or describe the boundary you want…'
},
// 8 — When to report back.
{
  key: 'cadence',
  question: ['**And when do you want to hear from me?**', 'I\'ll pull together what changed across everything I\'m watching, and send it on this schedule.'],
  choices: ['Monday, 8am', 'Every weekday, 8am', 'Friday, 5pm', 'Only when something needs me'],
  placeholder: 'Or give me your own schedule…'
}];

/** Resolve the chips for a step, given the role from step one. */
export function stepChoices(step: InterviewStep, role: InterviewRole | null): string[] {
  if (step.choicesByRole) {
    return step.choicesByRole[role ?? 'Operations'] ?? [];
  }
  return step.choices ?? [];
}

/** What the interview produced, shown as the closing turn. */
export const ONBOARDING_SUMMARY = {
  work: [{
    icon: 'folder',
    label: 'Opened your tracks'
  }, {
    icon: 'googlecalendar',
    label: 'Scheduled your pulses'
  }],
  answer: ['**That\'s me set up.** Here\'s what\'s running.', 'I\'ll keep all of it current as work happens — you won\'t have to maintain any of it. Ask me for something now and I\'ll go and do it.'],
  created: [{
    icon: 'folder',
    title: 'Tracks opened',
    detail: 'Filling from your mail, calendar and files already'
  }, {
    icon: 'radar',
    title: 'Pulses scheduled',
    detail: 'Running on their own — pause or retime any of them'
  }, {
    icon: 'file',
    title: '4 memory files written',
    detail: 'company/overview.md, team/sarah.md, and 2 more'
  }, {
    icon: 'plug',
    title: 'Connections queued',
    detail: 'I\'ll walk you through each one when you\'re ready'
  }]
} as const;
